import { Close as ClearIcon } from '@mui/icons-material';
import { InputBase, styled, Theme } from '@mui/material';
import React, { useState, useCallback, useRef, FormEvent, useEffect, CSSProperties } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';

import { SearchIcon } from '@ocx/ui-icons';

import { IconButton } from './inputs/icon-button/icon-button.component';
import { SizingMultiplier } from './sizing-multiplier.constant';

export const getTransparentStylesForWrapper: (theme: Theme) => CSSProperties = (theme) => ({
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  color: theme.palette.primary.contrastText,
});

export const getTransparentStylesForInput: (theme: Theme) => CSSProperties = (theme) => ({
  color: theme.palette.primary.contrastText,
});

const Form = styled('form')<{ getOverrides?: (theme: Theme) => CSSProperties }>(({ theme, getOverrides }) => ({
  background: theme.palette.background.paper,
  borderRadius: 8,
  boxShadow: theme.shadows[2],
  paddingLeft: theme.spacing(SizingMultiplier.sm),
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  zIndex: 1, // need to display search on top of "ListView",
  minHeight: 48, // by design
  ...getOverrides?.(theme),
}));

type SearchFieldProps = {
  onSubmit(search: string): void;
  value?: string;
  hideSearchIcon?: boolean;
  clearTriggersOnSubmit?: boolean;
  getWrapperOverrides?: (theme: Theme) => CSSProperties;
  getTransparentStylesForInput?: (theme: Theme) => CSSProperties;
  placeholder?: MessageDescriptor;
};

const placeholderText = defineMessages({
  'storeLocator:searchFieldPlaceholder': {
    id: 'storeLocator:searchFieldPlaceholder',
    defaultMessage: 'Search',
    description: 'Store locator search field placeholder',
  },
});

export const SearchField: React.FC<SearchFieldProps> = (props) => {
  const intl = useIntl();
  const {
    onSubmit,
    value = '',
    hideSearchIcon = false,
    getWrapperOverrides,
    getTransparentStylesForInput,
    clearTriggersOnSubmit,
    placeholder = placeholderText['storeLocator:searchFieldPlaceholder'],
  } = props;
  const input = useRef<HTMLInputElement | null>(null);
  const [searchString, setSearch] = useState(value);

  const clearSearch = useCallback(() => {
    if (clearTriggersOnSubmit) {
      onSubmit('');
    }
    setSearch('');
    input.current?.focus();
  }, [clearTriggersOnSubmit, onSubmit]);

  const onSearchSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      onSubmit(searchString);
      // Remove focus to hide keyboard
      input.current?.blur();
    },
    [onSubmit, searchString],
  );

  useEffect(() => {
    setSearch(value);
  }, [value]);

  return (
    // `action=""` - need it for update submit button text in mobile keyboard
    <Form action="" getOverrides={getWrapperOverrides} onSubmit={onSearchSubmit}>
      {hideSearchIcon ? undefined : <SearchIcon sx={{ color: 'action.active' }} />}

      <InputBase
        value={searchString}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={intl.formatMessage(placeholder)}
        sx={(theme: Theme) => ({
          p: (theme) => theme.spacing(SizingMultiplier.sm, 0, SizingMultiplier.sm, SizingMultiplier.sm),
          flexGrow: 1,
          ...getTransparentStylesForInput?.(theme),
        })}
        inputProps={{ 'aria-label': 'search', type: 'search' }}
        inputRef={input}
      />

      {searchString && (
        <IconButton onClick={clearSearch} color="inherit">
          <ClearIcon />
        </IconButton>
      )}
    </Form>
  );
};
